<template>
  <div class="container-fluid">
    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Usuarios de la tienda online</h1>
          <p class="view-header__subtitle">Gestioná los usuarios registrados en el e-commerce y su vínculo con clientes.</p>
        </div>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="filters-panel">
      <div class="filters-panel__section">
        <div class="filters-panel__section-title">
          <i class="bi bi-search"></i>
          Buscador
        </div>
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <input v-model="filters.q" type="search" class="form-control" placeholder="Buscar...">
          </div>
        </div>
      </div>
    </div>

    <!-- TABLA -->
    <div class="data-table">
      <div class="table-responsive">
        <table class="table mb-0">
          <thead>
            <tr role="row">
              <th>Fecha</th>
              <th>Usuario</th>
              <th>Email</th>
              <th>Cliente</th>
              <th>Estado</th>
              <th class="text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="users.length < 1">
              <td colspan="6" class="data-table__empty">No hay registros disponibles</td>
            </tr>
            <tr v-for="user in users" :key="user.id" role="row">
              <td>{{ user.created_at }}</td>
              <td><span class="data-table__primary">{{ user.name }}</span></td>
              <td class="data-table__cell-soft">{{ user.email }}</td>
              <td>
                <span v-if="user.client == null" class="data-table__muted">Sin asociar</span>
                <span v-else class="data-table__primary">{{ user.client.name }}</span>
              </td>
              <td>
                <span v-if="user.active" class="status-badge status-badge--success">
                  <i class="bi bi-check-circle-fill"></i>
                  Activo
                </span>
                <span v-else class="status-badge status-badge--danger">
                  <i class="bi bi-slash-circle-fill"></i>
                  Bloqueado
                </span>
              </td>
              <td class="text-right">
                <div class="table-action-group">
                  <button
                    type="button"
                    class="table-action-btn"
                    :title="user.active ? 'Bloquear acceso' : 'Habilitar acceso'"
                    @click="toggleActive(user)"
                  >
                    <i :class="user.active ? 'bi bi-lock' : 'bi bi-unlock'"></i>
                  </button>
                  <button
                    type="button"
                    class="table-action-btn"
                    :title="user.client == null ? 'Asignar cliente' : 'Reasignar cliente'"
                    @click="openClientModal(user)"
                  >
                    <i :class="user.client == null ? 'bi bi-person-plus' : 'bi bi-person-gear'"></i>
                  </button>
                  <button
                    type="button"
                    class="table-action-btn"
                    title="Eliminar"
                    @click="deleteUser(user.id)"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- PAGINACION -->
      <div class="data-table__footer">
        <div class="data-table__info">
          Página {{ filters.page }} de {{ totalPages || 1 }}
        </div>
        <ul class="data-table__pagination">
          <li class="page-item" :class="{ 'disabled': filters.page == 1 }">
            <a class="page-link" href="#" @click.prevent="prevPage()">Anterior</a>
          </li>
          <li v-if="(filters.page - (pageInterval / 2)) > 0" class="page-item">
            <a class="page-link" href="#" @click.prevent="prevPage()">...</a>
          </li>
          <template v-for="index in totalPages">
            <li
              v-if="inIntervalPage(index)"
              :key="index"
              class="page-item"
              :class="{ 'active': filters.page == index }"
            >
              <a class="page-link" href="#" @click.prevent="changePage(index)">{{ index }}</a>
            </li>
          </template>
          <li v-if="totalPages > (filters.page + (pageInterval / 2))" class="page-item">
            <a class="page-link" href="#" @click.prevent="nextPage()">...</a>
          </li>
          <li class="page-item" :class="{ 'disabled': filters.page >= totalPages }">
            <a class="page-link" href="#" @click.prevent="nextPage()">Siguiente</a>
          </li>
        </ul>
        <div class="data-table__page-size">
          <label class="mb-0">Mostrar</label>
          <q-select
            v-model="filters.itemsPerPage"
            :options="[{ value: 15, label: '15' }, { value: 25, label: '25' }, { value: 50, label: '50' }]"
          />
          <span>registros</span>
        </div>
      </div>
    </div>

    <!-- MODAL REASIGNAR CLIENTE -->
    <transition v-if="clientModal" name="modal">
      <div class="qp-modal-mask" @click.self="closeClientModal()">
        <div class="qp-modal">
          <div class="qp-modal__header">
            <h5 class="qp-modal__title">Asignar cliente</h5>
            <button type="button" class="qp-modal__close" aria-label="Cerrar" @click="closeClientModal()">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="qp-modal__body" v-if="clients">
            <div class="qp-modal__field">
              <label class="qp-modal__label">Cliente</label>
              <Multiselect
                v-model="selectedClient"
                :options="clients"
                :searchable="true"
                label="name"
                track-by="id"
                placeholder="Asignar cliente"
                :allow-empty="true"
                :close-on-select="true"
              />
            </div>

            <div class="qp-modal__field">
              <label class="qp-modal__label">Email asociado</label>
              <div class="qp-modal__inline">
                <input type="email" class="form-control" :value="mailText" disabled>
                <button type="button" class="btn view-header__btn" @click="searchMail()">
                  <i class="bi bi-envelope"></i>
                  Buscar por mail
                </button>
                <button type="button" class="table-action-btn" title="Limpiar selección" @click="cleanClient()">
                  <i class="bi bi-x-circle"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="qp-modal__footer">
            <button type="button" class="btn view-header__btn" @click="closeClientModal()">Cancelar</button>
            <button type="button" class="btn view-header__btn view-header__btn--primary" :disabled="isSaving" @click="saveClient()">
              <i class="bi bi-save"></i>
              Guardar
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
<script>
import Multiselect from 'vue-multiselect';
import QSelect from '@/components/shared/QSelect.vue';
export default {
  components: {Multiselect, QSelect},
  data() {
    return {
        users:[],
        filters:{
            q:'',
            itemsPerPage:15,
            page:1,
        },
        totalPages:0,
        pageInterval:22,
        clientModal:false,
        clients:[],
        userToEdit:null,
        selectedClient:null,
        mailText:'Sin asignar',
        isSaving:false,
    }
  },
  mounted() {
      this.getClients();
      this.getUsers();
  },
  methods: {
    getUsers(){
        let vm = this;
        this.$http.post("/api/ecom/users/getUsers", this.filters)
            .then((response) => {
                if (response.data.success) {
                    vm.users = response.data.data.list;
                    vm.totalPages=response.data.data.totalPages;
                } else {
                  // no-op intencional
                    
                }
            }).catch((error) => {
                console.log('error: '+error);
        })
    },
    getClients(){
        let vm = this;
        this.$http.get("/api/clients/getMailList")
            .then((response) => {
                if (response.data.success) {
                    vm.clients = response.data.data;
                }
            }).catch((error) => {
                console.log('error: '+error);
        })
    },
    toggleActive(user){
        let message=(user.active) ? 'Confirme si desea DESACTIVAR el logueo de este usuario' : 'Confirme si desea PERMITIR el acceso a este usuario';
        if(confirm(message)==true){
            let vm = this;
            this.$http.get("/api/ecom/users/toggleActive/" + user.id)
            .then(() => {
                
                vm.getUsers();
            }).catch((error) => {
                console.log('error: '+error);
            })
        }
    },
    openClientModal(user){
        this.userToEdit=user;
        if(user.client!==null){
            let client =  this.clients.filter(e => e.id === user.client.id);
            this.selectedClient = client[0];
        }
        this.clientModal=true;
    },
    closeClientModal(){
        this.userToEdit=null;
        this.selectedClient=null;
        this.mailText='Sin asignar';
        this.clientModal=false;
    },
    searchMail(){
        let result =  this.clients.filter(e => e.email === this.userToEdit.email);
        if(result.length===1){
            this.selectedClient= result[0];
            this.mailText = result[0].email;
        }else{
            this.mailText='Mail no encontrado';
        }
    },
    cleanClient(){
        this.selectedClient=null;
        this.mailText='Sin asignar';
    },
    saveClient(){
        if (this.isSaving) return;
        this.isSaving = true;
        let vm = this;
        let outdata={
            'user' : vm.userToEdit.id,
            'client' : (vm.selectedClient!==null) ? vm.selectedClient.id : null,
        };
        this.$http.post("/api/ecom/users/setClient", outdata)
            .then((response) => {
                if (response.data.success) {

                    vm.closeClientModal();
                    vm.getUsers();
                } else {
                  // no-op intencional

                }
            }).catch((error) => {
                console.log('error: '+error);
        }).finally(() => {
            vm.isSaving = false;
        })
    },
    deleteUser(id){
        if(confirm('Esta acción eliminará el registro PERMANENTEMENTE, ¿Desea continuar?')==true){
            let vm=this;
            this.$http.get("/api/ecom/users/deleteUser/" + id)
            .then(() => {
                
                vm.getUsers();
            }).catch((error) => {
                console.log('error: '+error);
            })
        }
    },
    prevPage(){
        if(this.filters.page>1){
            this.page = this.page-1;
            this.getUsers();
        }
    },
    nextPage(){
        if (this.filters.page<this.totalPages){
            this.page = this.page +1;
            this.getUsers();
        }
    },
    changePage(data){
        this.page = data;
        this.getUsers();
    },
    inIntervalPage(index){
        if(this.totalPages<=this.pageInterval){return true;}

        let limitprev = this.filters.page - (this.pageInterval/2);
        let limitnext = this.filters.page + (this.pageInterval/2);

        if(limitprev<0){
            limitprev=0;
            limitnext=this.pageInterval;
        }

        if(index>limitprev && index<limitnext){
            return true;
        }

        return false;
    },
  },
  watch: {
      'filters.q': {handler: function () {this.getUsers();}},
      'filters.itemsPerPage': {handler: function () {this.getUsers();}},
      'selectedClient':{handler: function(a){this.mailText=a.email}}
  },
}
</script>

<style lang="css">
.qp-modal-mask {
    position: fixed;
    inset: 0;
    z-index: 9999;
    background-color: rgba(16, 24, 40, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    transition: opacity .3s ease;
}

.qp-modal {
    width: 100%;
    max-width: 520px;
    background-color: var(--surface);
    border-radius: 12px;
    box-shadow: 0 12px 32px rgba(16, 24, 40, 0.18);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition: all .3s ease;
}

.qp-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border-soft);
}

.qp-modal__title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--text);
}

.qp-modal__close {
    border: 0;
    background: transparent;
    color: var(--text-muted);
    font-size: 16px;
    line-height: 1;
    padding: 4px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color .15s, color .15s;
}

.qp-modal__close:hover,
.qp-modal__close:focus {
    background: var(--surface-hover);
    color: var(--text);
    outline: none;
}

.qp-modal__body {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.qp-modal__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.qp-modal__label {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    color: var(--text-soft);
}

.qp-modal__inline {
    display: flex;
    align-items: center;
    gap: 8px;
}

.qp-modal__inline .form-control {
    flex: 1 1 auto;
    min-width: 0;
}

.qp-modal__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 16px 20px;
    border-top: 1px solid var(--border-soft);
    background: var(--surface-subtle);
}

.modal-enter {
    opacity: 0;
}

.modal-leave-active {
    opacity: 0;
}

.modal-enter .qp-modal,
.modal-leave-active .qp-modal {
    -webkit-transform: scale(1.05);
    transform: scale(1.05);
}
</style>
